import React, { useState, useEffect } from 'react';
import { Badge, Tooltip, Button, Card, Typography, Space } from 'antd';
import { 
  WifiOutlined, 
  DisconnectOutlined, 
  ReloadOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import simpleErrorHandler from '../utils/simpleErrorHandler';

const { Text } = Typography;

interface SocketStatusProps {
  showDetails?: boolean;
}

const SocketStatus: React.FC<SocketStatusProps> = ({ showDetails = false }) => {
  const [socket, setSocket] = useState<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [lastError, setLastError] = useState<string | null>(null);
  const [errorCount, setErrorCount] = useState(0);
  const [lastConnectTime, setLastConnectTime] = useState<Date | null>(null);

  useEffect(() => {
    const initSocket = () => {
      if ((window as any).io) {
        const socketInstance = (window as any).io;
        setSocket(socketInstance);

        // 监听连接事件
        socketInstance.on('connect', () => {
          console.log('🔌 Socket.IO 连接成功');
          setIsConnected(true);
          setConnectionState('connected');
          setLastConnectTime(new Date());
          setErrorCount(0);
          setLastError(null);
        });

        socketInstance.on('disconnect', (reason: string) => {
          console.log('🔌 Socket.IO 断开连接:', reason);
          setIsConnected(false);
          setConnectionState('disconnected');
          setLastError(reason);
        });

        socketInstance.on('connect_error', (error: any) => {
          console.error('🔌 Socket.IO 连接错误:', error);
          setIsConnected(false);
          setConnectionState('error');
          setErrorCount(prev => prev + 1);
          setLastError(error.message || '连接错误');
          
          // 记录到错误日志
          simpleErrorHandler.logError(error, 'SocketStatus');
        });

        socketInstance.on('reconnect', (attemptNumber: number) => {
          console.log('🔌 Socket.IO 重连成功，尝试次数:', attemptNumber);
          setIsConnected(true);
          setConnectionState('reconnected');
          setLastConnectTime(new Date());
        });

        socketInstance.on('reconnect_attempt', (attemptNumber: number) => {
          console.log('🔌 Socket.IO 重连尝试:', attemptNumber);
          setConnectionState('reconnecting');
        });

        socketInstance.on('reconnect_error', (error: any) => {
          console.error('🔌 Socket.IO 重连错误:', error);
          setConnectionState('reconnect_error');
          setLastError(error.message || '重连错误');
        });

        socketInstance.on('reconnect_failed', () => {
          console.error('🔌 Socket.IO 重连失败');
          setConnectionState('reconnect_failed');
          setLastError('重连失败');
        });

        // 检查初始连接状态
        setIsConnected(socketInstance.connected);
        setConnectionState(socketInstance.connected ? 'connected' : 'disconnected');
      }
    };

    // 延迟初始化，确保Socket.IO已加载
    const timer = setTimeout(initSocket, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected': return 'success';
      case 'reconnected': return 'success';
      case 'reconnecting': return 'processing';
      case 'error': return 'error';
      case 'reconnect_error': return 'error';
      case 'reconnect_failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (connectionState) {
      case 'connected':
      case 'reconnected':
        return <CheckCircleOutlined />;
      case 'reconnecting':
        return <ClockCircleOutlined spin />;
      case 'error':
      case 'reconnect_error':
      case 'reconnect_failed':
        return <ExclamationCircleOutlined />;
      default:
        return <DisconnectOutlined />;
    }
  };

  const getStatusText = () => {
    switch (connectionState) {
      case 'connected': return '已连接';
      case 'reconnected': return '重连成功';
      case 'reconnecting': return '重连中...';
      case 'error': return '连接错误';
      case 'reconnect_error': return '重连错误';
      case 'reconnect_failed': return '重连失败';
      default: return '未连接';
    }
  };

  const handleReconnect = () => {
    if (socket) {
      console.log('🔌 手动重连 Socket.IO');
      socket.disconnect();
      socket.connect();
    }
  };

  if (showDetails) {
    return (
      <Card size="small" title="Socket.IO 连接状态" style={{ marginBottom: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Badge status={getStatusColor()} />
            <Text>{getStatusText()}</Text>
            <Button 
              size="small" 
              icon={<ReloadOutlined />} 
              onClick={handleReconnect}
              disabled={!socket}
            >
              重连
            </Button>
          </div>
          
          {lastConnectTime && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              最后连接: {lastConnectTime.toLocaleTimeString()}
            </Text>
          )}
          
          {errorCount > 0 && (
            <Text type="danger" style={{ fontSize: '12px' }}>
              错误次数: {errorCount}
            </Text>
          )}
          
          {lastError && (
            <Text type="danger" style={{ fontSize: '12px' }}>
              最后错误: {lastError}
            </Text>
          )}
        </Space>
      </Card>
    );
  }

  return (
    <Tooltip title={`Socket.IO: ${getStatusText()}`}>
      <div 
        style={{ 
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative'
        }}
        onClick={handleReconnect}
      >
        {getStatusIcon()}
        <div 
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: getStatusColor() === 'success' ? '#52c41a' : 
                           getStatusColor() === 'error' ? '#ff4d4f' : '#faad14',
            marginTop: '2px',
            boxShadow: '0 0 2px rgba(0,0,0,0.2)'
          }}
        />
      </div>
    </Tooltip>
  );
};

export default SocketStatus;
